import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import Lightbox from '../components/Lightbox';

export default function ReferenceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadReferenceData() {
      setLoading(true);
      try {
        const referencesData = await fetchCollection('references');
        const foundRef = referencesData.find(r => r.slug.en === slug);
        
        if (!foundRef) {
          navigate('/404');
          return;
        }

        setReference(foundRef);
      } catch (error) {
        console.error('Error loading reference details:', error);
      } finally {
        setLoading(false);
      }
    }
    loadReferenceData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner-third animate-spin text-4xl text-accent" />
          <p className="text-slate-500 font-medium">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!reference) return null;

  return (
    <div className="pt-[73px] bg-white min-h-screen">
      {/* Hero Header */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-15">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${reference.images[0]}`}
            alt={reference.title}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-3xl space-y-4">
            <Link to="/references" className="inline-flex items-center gap-2 text-xs font-semibold text-accent hover:text-accent-light transition-colors duration-200 uppercase tracking-widest">
              <i className="fa-regular fa-arrow-left" />
              <span>Back to References</span>
            </Link>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
              {reference.title}
            </h1>
            <div className="flex flex-wrap gap-6 text-sm text-slate-300 pt-2">
              <div className="flex items-center gap-2">
                <i className="fa-regular fa-user-tie text-accent" />
                <span>Client: <strong className="text-white">{reference.client}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-regular fa-calendar-range text-accent" />
                <span>Duration: <strong className="text-white">{reference.duration}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column: Rich Text Content & Images */}
          <div className="lg:col-span-2 space-y-12">
            <div className="space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-primary font-display">Project Overview</h2>
              <div 
                className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:text-primary prose-p:leading-relaxed prose-p:text-slate-600 prose-p:text-base prose-li:text-slate-600"
                dangerouslySetInnerHTML={{ __html: reference.content }}
              />
            </div>

            {/* Project Images Grid */}
            {reference.images.length > 0 && (
              <div className="pt-8 border-t border-slate-100 space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-primary font-display">Project Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {reference.images.map((img, index) => (
                    <div 
                      key={index}
                      className="group relative aspect-video rounded-xl overflow-hidden border border-slate-100 shadow-sm cursor-pointer"
                      onClick={() => setLightboxIndex(index)}
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=250,fit=cover,quality=80,format=auto/${img}`}
                        alt={`${reference.title} ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <i className="fa-regular fa-magnifying-glass-plus text-white text-xl" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8">
            {/* Core Benefits */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100/80 space-y-6">
              <h3 className="font-display text-xs font-bold tracking-widest text-accent uppercase">Core Benefits</h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-user-tie-hair text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">ONE POINT OF CONTACT</span>
                    <span className="text-xs text-slate-500 mt-1 block">Streamlined communication with a dedicated liaison.</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-clock text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">LESS TRAVEL TIME</span>
                    <span className="text-xs text-slate-500 mt-1 block">Accommodations established close to the work site.</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-shield-check text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">INCREASED SAFETY</span>
                    <span className="text-xs text-slate-500 mt-1 block">Certified, fully approved facilities and operations.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Contact CTA */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100/80 text-center space-y-4">
              <h3 className="font-display text-lg font-bold text-primary">Need a similar setup?</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Contact us today to discuss how we can customize our solutions for your project site.
              </p>
              <Link
                to="/contact"
                className="block w-full py-3 px-6 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-sm"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for Gallery */}
      {lightboxIndex !== null && (
        <Lightbox
          images={reference.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
